import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { trackEvent } from './mixpanel';

const apiUrl = process.env.REACT_APP_API_URL;
async function getKey({ keyStartsWith, queryClient, positionOfKey, total }) {
    try {
        const queryKeys = await queryClient?.getQueryCache().findAll({ queryKey: keyStartsWith });
        let x = queryKeys?.filter(key => key?.queryKey?.length > positionOfKey && key.queryKey[positionOfKey]).map(key => {

            let pagination = key.queryKey[positionOfKey];
            queryClient?.setQueryData(key.queryKey, (oldData) => {
                return {
                    ...oldData,
                    pagination: {
                        ...oldData?.pagination,
                        total: total
                    }
                }
            })
            if (key && key?.state?.data?.data?.length >= 10) {
                pagination += 1;
            }
            return pagination;
        }
        ).sort((a, b) => b - a)[0]
        if (total <= 10) {
            return 1;
        }
        if (Math.ceil(total / 10) === x) {
            return x;
        }
        return null
    }
    catch (err) {
        console.error(err)
    }
}
async function refreshCacheKey({ keyStartsWith, queryClient, positionOfKey, total }) {
    try {
        const queryKeys = await queryClient?.getQueryCache().findAll({ queryKey: keyStartsWith });
        queryKeys.forEach(async (data) => {
            if (data?.queryKey?.length <= positionOfKey) {
                return;
            }
            await queryClient?.refetchQueries(data.queryKey);
            if (data?.state?.data?.data?.length == 0) {
                queryClient.removeQueries({ queryKey: data.queryKey });
            }
            else {
                queryClient?.setQueryData(data.queryKey, (oldData) => {
                    return {
                        ...oldData,
                        pagination: {
                            ...oldData?.pagination,
                            total: total
                        }
                    }
                });
            }
        })
    }
    catch (err) {
        console.error(err);
    }
}
// Auth
export const useOtp = () => {
    return useMutation({
        mutationFn: async ({ email, otp }) => {
            const response = await axios.post(`${apiUrl}/user/otp`, {
                email, otp
            });
            return response.data;
        },
        // retry: 0,
        // cacheTime: 0,
    });
};

export const useLogin = () => {
    return useMutation({
        mutationFn: async ({ email, password, showV2, recaptchaToken }) => {
            const response = await axios.post(`${apiUrl}/user/login`, {
                email,
                password,
                showV2,
                recaptchaToken
            });
            return response.data;
        },
        // retry: 0,
        // cacheTime: 0,   
    });
};

export const useLogout = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await axios.post(`${apiUrl}/auth/logout`, {}, {
                withCredentials: true,
            });
        },
        onSuccess: () => {
            queryClient.setQueryData(['user'], null);
            queryClient.clear();
        },
    });
};

export const useCheckAuth = () => {
    return useQuery({
        queryKey: ['user'],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/auth/check`, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data;
        },
        retry: false,
    });
};

// User

export const useGetUser = (userId) => {
    return useQuery({
        queryKey: ['user'],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/user/${userId}`, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data;
        },
        enabled: !!userId,
        staleTime: 1000 * 60 * 60 * 24,
    });
};

export const useGetAllUsers = (page, filters = {}, companyName, forSendEmail = false) => {
    return useQuery({
        queryKey: ['users', page, filters, companyName, forSendEmail],
        queryFn: async () => {
            let url = `${apiUrl}/user`;
            const params = new URLSearchParams();
            if (page) {
                params.append('page', page);
                params.append('limit', 10);
            }
            if (filters.role) params.append('role', filters.role.join(','));
            if (filters.status) params.append('status', filters.status.join(','));
            if (filters.first_name) params.append('textsearch', filters.first_name);
            if (filters.startDate && filters.endDate) {
                params.append('startDate', filters.dateRange.start);
                params.append('endDate', filters.dateRange.end);
            }
            if (companyName) params.append('companyName', companyName);
            if (forSendEmail) params.append('forSendEmail', 'true');

            const queryString = params.toString();
            if (queryString) url += `?${queryString}`;
            const response = await axios.get(url, { withCredentials: true });
            return {
                data: response.data.response,
                pagination: {
                    current: response.data.page,
                    pageSize: response.data.limit,
                    total: response.data.totalUsers
                }
            };
            
        },
        refetchOnMount: 'always',
    });
};

export const useGetAllPrimaryClientsUsers = (company) => {
    return useQuery({
        queryKey: ['getAllPrimaryClients', company],
        queryFn: async () => {
            let url = `${apiUrl}/user/primary_client`;

            if (company) {
                url += `?company=${company}`
            }
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data.response;
        },
        enabled: !!company,
    });
}

// export const useGetAllUsers = () => {
//     return useInfiniteQuery({
//         queryKey: ['users'],
//         initialPageParam: 1,
//         queryFn: async ({ pageParam = 1 }) => {
//             const response = await axios.get(`${apiUrl}/user`, {
//                 params: { page: pageParam },
//             }, {
//                 withCredentials: true
//             });
//             return response.data.response;
//         },
//     });
// };

export const useCreateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (userData) => {
            const response = await axios.post(`${apiUrl}/user`, userData.userData, {
                withCredentials: true
            });
            return response.data.response;
        },
        onSuccess: (newUser) => {
            trackEvent("Create User", {
                "_id": newUser?._id,
                "Email": newUser?.email,
                "First Name": newUser?.first_name,
                "Last Name": newUser?.last_name,
                "Role": newUser?.role
            });
            queryClient.setQueryData(['users'], (oldData) => {
                if (oldData) {
                    return [...oldData, newUser];
                }
                return [newUser];
            });
        },
        onError: (error) => {
            console.error(error)
        }
    });
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ userId, userData }) => {
            const response = await axios.patch(`${apiUrl}/user/${userId}`, userData, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: (updatedUser) => {
            trackEvent("Update User", {
                "_id": updatedUser?._id,
                "Email": updatedUser?.email,
                "First Name": updatedUser?.first_name,
                "Last Name": updatedUser?.last_name,
                "Role": updatedUser?.role
            })

            queryClient.setQueryData(['users'], (oldData) => {
                if (oldData) {
                    return oldData.map((user) =>
                        user._id === updatedUser._id ? updatedUser : user
                    );
                }
                return oldData;
            });
        },
    });
};

export const useDeleteUser = (apiUrl) => {
    const queryClient = useQueryClient();
    return useMutation(
        async (userId) => {
            await axios.delete(`${apiUrl}`);
        },
        {
            onSuccess: (_, userId) => {
                trackEvent("Delete User", {
                    "_id": userId
                })

                queryClient.setQueryData(['users'], (oldData) => {
                    if (oldData) {
                        return oldData.filter((user) => user._id !== userId);
                    }
                    return oldData;
                });
            },
        }
    );
};

// Financial Year

export const useGetAllFinancialYears = (clientName, companyName, page, filters = {}, primaryClient = {}) => {
    return useQuery({
        queryKey: ['financialYears', clientName?._id, companyName, page, filters],
        queryFn: async () => {
            let url = `${apiUrl}/fy`;
            const params = new URLSearchParams();
            if (clientName?.abn) {
                params.append('abn', clientName?.abn);
            }
            if (clientName?._id) {
                params.append('client_id', clientName?._id);
            }
            if (companyName) {
                params.append('company', companyName);
            }
            if (page) {
                params.append('page', page);
                params.append('limit', 10);
            }
            if (filters.title) {
                params.append('textsearch', filters.title);
            }
            if (filters.priority?.length) {
                params.append('priority', filters.priority.join(','));
            }
            if (filters.status?.length) {
                params.append('status', filters.status.join(','));
            }
            if (filters.fy_type?.length) {
                params.append('fy_type', filters.fy_type.join(','));
            }
            if (filters.period_type?.length) {
                params.append('period_type', filters.period_type.join(','));
            }
            if (filters.assigned_to?.length) {
                params.append('assigned_to', filters.assigned_to.join(','));
            }
            if (primaryClient?._id) {
                params.append('primary_client', primaryClient?._id)
            }
            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
            // `${process.env.REACT_APP_API_URL}/fy?client_id=${client_id}`
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return {
                data: response.data.response,
                pagination: {
                    current: response.data.page,
                    pageSize: response.data.limit,
                    total: response.data.totalFinancialYears
                }
            };
        },
    });
};

export const useCreateFinancialYear = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (financialYearData) => {

            const response = await axios.post(`${apiUrl}/fy`, financialYearData, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: async (newFinancialYear, financialYearDataPayload) => {
            trackEvent("Create Financial Year", {
                "_id": newFinancialYear?.response?._id,
                "client_id": newFinancialYear?.response?.client_id,
                "company": newFinancialYear?.response?.company,
                "title": newFinancialYear?.response?.title,
                "fy_type": newFinancialYear?.response?.fy_type,
                "period_type": newFinancialYear?.response?.period_type,
                "start_date": newFinancialYear?.response?.start_date,
                "end_date": newFinancialYear?.response?.end_date,
                "status": newFinancialYear?.response?.status
            });

            let cacheKeyPage = await getKey({ keyStartsWith: ['financialYears', financialYearDataPayload?.client_id, financialYearDataPayload?.company?._id], queryClient, positionOfKey: 3, total: newFinancialYear?.totalFinancialYears });
            if (cacheKeyPage === null) {
                return;
            }
            const cacheKey = ['financialYears', financialYearDataPayload?.client_id, financialYearDataPayload?.company?._id, cacheKeyPage, {}];
            queryClient.setQueryData(cacheKey, (oldData) => {
                const newData = oldData ? [...oldData?.data, newFinancialYear.response] : [newFinancialYear.response];
                return {
                    data: newData,
                    pagination: {
                        ...oldData?.pagination,
                        total: newFinancialYear?.totalFinancialYears,
                        current: cacheKeyPage
                    }
                }
            });
        },
    });
};

export const useUpdateFinancialYear = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            const response = await axios.patch(`${apiUrl}/fy/${payload.fy}`, payload, {
                withCredentials: true
            });
            return response.data.response;
        },
        onSuccess: (updatedFinancialYear, payload) => {
            trackEvent("Update Financial Year", {
                "_id": updatedFinancialYear?._id,
                "client_id": updatedFinancialYear?.client_id,
                "company": updatedFinancialYear?.company,
                "title": updatedFinancialYear?.title,
                "fy_type": updatedFinancialYear?.fy_type,
                "period_type": updatedFinancialYear?.period_type,
                "start_date": updatedFinancialYear?.start_date,
                "end_date": updatedFinancialYear?.end_date,
                "status": updatedFinancialYear?.status
            });
            const key = payload.all
                ? ["financialYears", null, payload.companyName, payload.page, payload.filters]
                : ["financialYears", payload.clientName, null, payload.page, payload.filters];
            queryClient.setQueryData(key, (oldData) => {
                if (!oldData) return oldData;
                return {
                    ...oldData,
                    data: oldData.data.map((financialYear) =>
                        financialYear._id === updatedFinancialYear._id
                            ? updatedFinancialYear
                            : financialYear
                    )
                };
            });
        },
    });
};

export const useDeleteFinancialYear = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            const response = await axios.delete(`${apiUrl}/fy/${payload?.fy}?client=${payload?.client}`, payload, {
                data: payload,
                withCredentials: true
            })
            return response
        },
        onSuccess: async (response, payload) => {
            trackEvent("Delete Financial Year", {
                "_id": payload?.fy
            })
            refreshCacheKey({ keyStartsWith: ['financialYears'], queryClient, positionOfKey: 3, total: response?.data?.totalFinancialYears });
        },
    }
    );
};

// Company

export const useGetAllCompanies = (user) => {
    return useQuery({
        queryKey: ['companies'],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/company`, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data.response;
        },
        enabled: !!user,
    });
};

export const useGetCompanyWithData = (user, page, filters = {}) => {
    return useQuery({
        queryKey: ['companyData', page, filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('page', page);
            if (filters.name) {
                params.append('name', filters.name);
            }
            const url = `${apiUrl}/company/data?${params.toString()}`;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data.response;
        },
        enabled: !!user,
    });
}


export const useCreateCompany = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (companyData) => {
            const response = await axios.post(`${apiUrl}/company`, companyData, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: (newCompany) => {
            trackEvent("Create Company", {
                "_id": newCompany?._id,
                "name": newCompany?.name
            })
            queryClient.setQueryData(['companies'], (oldData) => {
                if (oldData) {
                    return [...oldData, newCompany];
                }
                return [newCompany];
            });
        },
    });
};

export const useUpdateCompany = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, name }) => {
            const response = await axios.patch(`${apiUrl}/company/${id}`, { name }, {
                withCredentials: true
            });
            return response.data;
        },

        onSuccess: (updatedCompany) => {
            trackEvent("Update Company", {
                "_id": updatedCompany?._id,
                "name": updatedCompany?.name
            })
            queryClient.setQueryData(['companies'], (oldData) => {
                if (oldData) {
                    return oldData.map(company =>
                        company._id === updatedCompany._id ? updatedCompany : company
                    );
                }
                return oldData;
            });
        },
    }
    );
};

export const useDeleteCompany = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (companyId) => {
            const res = await axios.delete(`${apiUrl}/company/${companyId}`, {
                withCredentials: true
            });
            return res.data;
        },
        onSuccess: (_, companyId) => {
            trackEvent("Delete Company", {
                "_id": companyId
            })

            queryClient.setQueryData(['companies'], (oldData) => {
                if (oldData) {
                    return oldData.filter((company) => company._id !== companyId);
                }
                return oldData;
            });
        }
    });
};

// Client

export const useGetAllClients = (companyName, page, filters = {}, primaryClient = {}) => {
    const companyNameWithoutDash = companyName?.replace(/-/g, " ")
    return useQuery({
        queryKey: ['clients', companyNameWithoutDash, page, filters],
        queryFn: async () => {
            let limit = 10;
            let url = `${apiUrl}/client`;
            const params = new URLSearchParams();

            if (page) {
                params.append('page', page);
                params.append('limit', limit);
            }

            if (companyName) {
                params.append('cname', companyNameWithoutDash);
            }
            if (filters?.entity_type) {
                params.append('entity_type', filters.entity_type.join(','));
            }
            if (filters?.title) {
                params.append('textsearch', filters.title);
            }
            if (primaryClient?._id) {
                params.append('primary_client', primaryClient?._id);
            }

            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });

            return {
                data: response.data.response,
                pagination: {
                    current: response.data.page,
                    pageSize: response.data.limit,
                    total: response.data.totalClients,
                    entity_type: response.data.entityTypes,
                }
            };
        },
    });
};

export const useGetAllPrimaryClientsEntity = (companyName, page, filters = {}) => {
    return useQuery({
        queryKey: ['clients', companyName, page, filters],
        queryFn: async () => {
            let limit;
            if (page) {
                limit = 10
            }
            let url = companyName ? `${apiUrl}/client?cname=${companyName}&page=${page}&limit=${limit}` : `${apiUrl}/client?page=${page}&limit=${limit}`;
            if (filters?.title) {
                url += `&textsearch=${filters?.title}`
            }
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return {
                data: response.data.response,
                pagination: {
                    current: response.data.page,
                    pageSize: response.data.limit,
                    total: response.data.totalClients
                }
            };
        },
    });
};

export const useCreateClient = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (clientData) => {
            const response = await axios.post(`${apiUrl}/client`, clientData, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: async (newClient, payload) => {
            trackEvent("Create Client", {
                "_id": newClient?.response?._id,
                "title": newClient?.response?.title,
                "company": newClient?.response?.company,
                "abn": newClient?.response?.abn,
                "status": newClient?.response?.status,
                "entity_type": newClient?.response?.entity_type
            });
            let cacheKeyPage = await getKey({ keyStartsWith: ['clients', payload?.company_name], queryClient, positionOfKey: 2, total: newClient?.totalClients });
            if (cacheKeyPage === null) {
                return;
            }
            const cacheKey = ['clients', payload?.company_name, cacheKeyPage, {}];
            queryClient.setQueryData(cacheKey, (oldData) => {
                const newData = oldData ? [...oldData?.data, newClient.response] : [newClient.response];
                return {
                    data: newData,
                    pagination: {
                        ...oldData?.pagination,
                        total: newClient?.totalClients,
                        current: cacheKeyPage
                    }
                }
            });
        },
    });
};

export const useUpdateClient = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ client_id, ...rest }) => {
            const response = await axios.patch(`${apiUrl}/client/${client_id}`, rest, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: (updatedClient) => {
            trackEvent("Update Client", {
                "_id": updatedClient?._id,
                "title": updatedClient?.title,
                "company": updatedClient?.company?.name,
                "abn": updatedClient?.abn,
                "status": updatedClient?.status,
                "entity_type": updatedClient?.entity_type
            })
            queryClient.setQueryData(['clients', updatedClient.company.name], (oldData) => {
                if (oldData) {
                    return oldData.map((client) =>
                        client._id === updatedClient._id ? updatedClient : client
                    );
                }
                return oldData;
            });
        },
    }
    );
};

export const useDeleteClient = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn:
            async (payload) => {
                let data = await axios.delete(`${apiUrl}/client/${payload?.client?._id}?company=${payload?.company?._id}`, {
                    withCredentials: true,
                });
                return data;
            },
        onSuccess: async (data, payload) => {
            trackEvent("Delete Client", {
                "_id": payload?.client?._id
            })
            const key = ['clients']
            // queryClient.setQueryData(key, (oldData) => {
            //     if (oldData) {
            //         return oldData.filter((item) => item._id !== payload.client._id);
            //     }
            //     return oldData;
            // });
            await queryClient.invalidateQueries({ queryKey: ['clients'] })
            refreshCacheKey({ keyStartsWith: key, queryClient, positionOfKey: 2, total: data?.data?.totalClients });
        },
    }
    );
};

// Queries

export const useGetAllQueries = (fy, type, page, primary_client_query, filters = {}) => {
    return useQuery({
        queryKey: ['queries', fy, type, page, filters],
        queryFn: async () => {
            let url = `${apiUrl}/query`;
            const params = new URLSearchParams();
            if (fy) {
                params.append('financialYear', fy);
            }
            if (type) {
                params.append('type', type)
            }
            if (typeof primary_client_query === "boolean") {
                params.append('primary_client_query', primary_client_query);
            }
            if (page) {
                params.append('page', page);
                params.append('limit', 10)
            }
            url = params.toString() ? `${url}?${params.toString()}` : url;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return {
                data: response.data.response,
                pagination: {
                    current: response.data.page,
                    pageSize: response.data.limit,
                    total: response.data.totalQueries
                }
            };
        },
        enabled: !!fy,
    });
};

export const useGetAllPrimaryQueries = (fy, type, page, primary_client_query, filters = {}, primaryClientId) => {
    return useQuery({
        queryKey: ['primary_client_queries', fy, type, page, filters, primaryClientId],
        queryFn: async () => {
            let url = `${apiUrl}/query`;
            const params = new URLSearchParams();
            if (fy) {
                params.append('financialYear', fy);
            }
            if (type) {
                params.append('type', type)
            }
            if (primaryClientId) {
                params.append('primary_client', primaryClientId);
            }
            if (typeof primary_client_query === "boolean") {
                params.append('primary_client_query', primary_client_query);
            }
            if (page) {
                params.append('page', page);
                params.append('limit', 10)
            }
            url = params.toString() ? `${url}?${params.toString()}` : url;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return {
                data: response.data.response,
                pagination: {
                    current: response.data.page,
                    pageSize: response.data.limit,
                    total: response.data.totalQueries
                }
            };
            // return response.data.response;
        },
        enabled: !!fy,
    });
};



export const useCreateQuery = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (queryData) => {
            const response = await axios.post(`${apiUrl}/query`, queryData, {
                withCredentials: true,
            });
            return response?.data;
        },
        onSuccess: async (newQuery, payload) => {
            trackEvent("Create Query", {
                "_id": newQuery?.response?._id,
                "type": newQuery?.response?.type,
                "financialYear": newQuery?.response?.financialYear,
                "primary_client_query": newQuery?.response?.primary_client_query,
                "s_no": newQuery?.response?.s_no,
                "title": newQuery?.response?.title,
                "status": newQuery?.response?.status,
                "company": newQuery?.response?.company,
            });
            let cacheKeyPage = await getKey({ keyStartsWith: ['queries', payload.financialYear, payload.type], queryClient, positionOfKey: 3, total: newQuery?.totalQueries });
            if (cacheKeyPage === null) {
                return;
            }
            const cacheKey = ['queries', payload.financialYear, payload.type, cacheKeyPage, {}];
            queryClient.setQueryData(cacheKey, (oldData) => {
                const newData = oldData ? [...oldData?.data, newQuery.response] : [newQuery.response];
                return {
                    data: newData,
                    pagination: {
                        ...oldData?.pagination,
                        total: newQuery?.totalClients
                    }
                }
            });
        },
    });
};

export const useUpdateQuery = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ queryId, queryData }) => {
            const response = await axios.put(`${apiUrl}/query/${queryId}`, queryData);
            return response.data;
        },
        {
            onSuccess: (updatedQuery) => {
                trackEvent("Update Query", {
                    "_id": updatedQuery?._id,
                    "type": updatedQuery?.type,
                    "financialYear": updatedQuery?.financialYear,
                    "primary_client_query": updatedQuery?.primary_client_query,
                    "s_no": updatedQuery?.s_no,
                    "title": updatedQuery?.title,
                    "status": updatedQuery?.status,
                    "company": updatedQuery?.company,
                })
                queryClient.setQueryData(['queries'], (oldData) => {
                    if (oldData) {
                        return oldData.map((query) =>
                            query._id === updatedQuery._id ? updatedQuery : query
                        );
                    }
                    return oldData;
                });
            },
        }
    );
};

export const useDeleteQuery = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (queryId) => {
            let data = await axios.delete(`${apiUrl}/query/${queryId}`, {
                withCredentials: true,
            });
            return data;
        },
        onSuccess: (_, queryId) => {
            trackEvent("Delete Query", {
                "_id": queryId
            });

            queryClient.invalidateQueries({ queryKey: ['queries'], exact: false });
            queryClient.invalidateQueries({ queryKey: ['primary_client_queries'], exact: false });

            return "Query Deleted Successfully";
        },
    });
};


export const useGetQueryById = (queryId, sub = false) => {
    return useQuery({
        queryKey: ['query', queryId, sub],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/query/${queryId}`, {
                params: { sub },
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data;
        },
        enabled: !!queryId,
    });
};
//Videos


export const useCreateVideo = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (videoData) => {
            const uri = videoData.toString()
            const response = await axios.post(`${apiUrl}/video`, { uri }, { withCredentials: true });
            const embedResponse = await fetch(`https://www.youtube.com/oembed?url=${response.data.uri}&format=json`);
            const embedData = await embedResponse.json();

            return {
                ...response.data,
                title: embedData.title,
                thumbnail: embedData.thumbnail_url
            };
        },
        onSuccess: (newVideo) => {
            trackEvent("Create Video", {
                "_id": newVideo?._id,
                "title": newVideo?.title,
                "thumbnail": newVideo?.thumbnail
            })
            queryClient.setQueryData(['videos'], (oldData) => {
                if (oldData) {
                    return [...oldData, newVideo];
                }
                return [newVideo];
            });
        },
    });
};

export const useGetAllVideos = () => {
    return useQuery({
        queryKey: ['videos'],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/video`, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            const videos = response.data.result;

            const videosWithMetadata = await Promise.all(
                videos.map(async (video) => {
                    if (video.title && video.thumbnail) {
                        return video;
                    }
                    try {
                        const embedResponse = await fetch(`https://www.youtube.com/oembed?url=${video.uri}&format=json`);
                        const embedData = await embedResponse.json();
                        return {
                            ...video,
                            title: embedData.title,
                            thumbnail: embedData.thumbnail_url
                        };
                    } catch (error) {
                        return {
                            ...video,
                            title: "Invalid Video",
                            thumbnail: ""
                        };
                    }
                })
            );

            return videosWithMetadata;
        },
    });
};

export const useDeleteVideo = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (videoId) => {
            await axios.delete(`${apiUrl}/video/${videoId}`, { withCredentials: true });
        },
        onSuccess: (_, videoId) => {
            trackEvent("Delete Video", {
                "_id": videoId
            })
            queryClient.setQueryData(['videos'], (oldData) => {
                if (oldData) {
                    return oldData.filter((video) => video._id !== videoId);
                }
                return oldData;
            });
        },
    });
};

export const useReorderVideos = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (reorderedVideos) => {
            const response = await axios.post(`${apiUrl}/video/reorder`, { reorderedVideos }, { withCredentials: true });
            const updatedVideos = response.data.result.map(video => {
                const existingVideo = reorderedVideos.find(v => v._id === video._id);
                return {
                    ...video,
                    title: existingVideo?.title || "Invalid Video",
                    thumbnail: existingVideo?.thumbnail || ""
                };
            });
            return updatedVideos;
        },
        onSuccess: (updatedVideos) => {
            queryClient.setQueryData(['videos'], updatedVideos);
        },
    });
};

// folder

export const useGetAllFolders = (fy_id, page) => {
    return useQuery({
        queryKey: ['folders', fy_id, page],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/folder?fy_id=${fy_id}&page=${page}`, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data.result;
        },
        enabled: !!fy_id,
        staleTime: 1000 * 60 * 5,
        cacheTime: 1000 * 60 * 10,
    });
};

export const useCreateFolder = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(`${apiUrl}/folder`, payload, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: (newFolder, payload) => {
            trackEvent("Create Folder", {
                "_id": newFolder?._id,
                "name": newFolder?.name,
                "parentFolder": newFolder?.parentFolder,
                "fy_id": newFolder?.fy_id
            })
            queryClient.setQueryData(['folders', payload.fy_id, payload.page], (oldData) => {
                if (oldData) {
                    return [...oldData, newFolder];
                }
                return [newFolder];
            });
        },
    });
};

export const useUpdateFolder = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            const { page, ...rest } = payload;
            const response = await axios.patch(`${apiUrl}/folder/${rest.id}`, rest, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: (newFolder, payload) => {
            trackEvent("Update Folder", {
                "_id": newFolder?._id,
                "name": newFolder?.name,
                "parentFolder": newFolder?.parentFolder,
                "fy_id": newFolder?.fy_id
            })
            queryClient.setQueryData(['folders', payload.fy_id, payload.page], (oldData) => {
                if (oldData) {
                    return oldData.map((item) => item._id === payload.id ? newFolder : item);
                }
                return oldData;
            });
        },
    });
};

export const useDeleteFolder = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (folderId) => {
            await axios.delete(`${apiUrl}/folder/${folderId}`);
        },
        onSuccess: (folderId) => {
            trackEvent("Delete Folder", {
                "_id": folderId
            })
            queryClient.invalidateQueries(['folders']);
        },
    });
};

// document

export const useCreateDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (documentData) => {
            try {
                const { page, ...rest } = documentData;
                const response = await axios.post(`${apiUrl}/document`, rest, {
                    withCredentials: true
                });
                return response.data;
            }
            catch (err) { }
        },
        onSuccess: (newDocument, documentData) => {
            trackEvent("Update Query", {
                "_id": newDocument?._id,
                "created_by": newDocument?.created_by,
                "proposed_entity": newDocument?.proposed_entity,
                "proposed_fy": newDocument?.proposed_fy,
                "status": newDocument?.status,
                "company": newDocument?.company,
                "client_id": newDocument?.client_id,
                "folder_id": newDocument?.folder_id,
                "folder_name": newDocument?.folder_name,
                "abn": newDocument?.abn,
            })
            queryClient.setQueryData(['documents', documentData.folder_id, documentData.page], (oldData) => {
                if (oldData?.data?.length) {
                    return [...oldData.data, newDocument];
                }
                return [newDocument];
            });
        },
        onError: (error) => {
            console.error(error)
            console.error("Error creating document:", error.response.data.message);
        },
    });
};

export const useGetAllDocuments = (folder_id, page) => {
    return useQuery({
        queryKey: ['documents', folder_id, page],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/document?folder_id=${folder_id}&page=${page}&limit=10`, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return {
                data: response.data.result,
                pagination: {
                    current: response.data.page,
                    pageSize: response.data.limit,
                    total: response.data.totalDocuments
                }
            };

        },
        staleTime: 1000 * 60 * 5,
    });
};

export const useUpdateDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ documentId, updateData }) => {
            const response = await axios.put(`${apiUrl}/document/${documentId}`, updateData, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: (updatedDocument) => {
            trackEvent("Update Query", {
                "_id": updatedDocument?._id,
                "created_by": updatedDocument?.created_by,
                "proposed_entity": updatedDocument?.proposed_entity,
                "proposed_fy": updatedDocument?.proposed_fy,
                "status": updatedDocument?.status,
                "company": updatedDocument?.company,
                "client_id": updatedDocument?.client_id,
                "folder_id": updatedDocument?.folder_id,
                "folder_name": updatedDocument?.folder_name,
                "abn": updatedDocument?.abn,
            })
            queryClient.invalidateQueries(['documents']);
            queryClient.setQueryData(['documents'], (oldData) => {
                if (oldData) {
                    return oldData.map((doc) =>
                        doc._id === updatedDocument._id ? updatedDocument : doc
                    );
                }
                return oldData;
            });
        },
        onError: (error) => {
            console.error("Error updating document:", error.response.data.message);
        },
    });
};

export const useDeleteDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (documentId) => {
            const response = await axios.delete(`${apiUrl}/document/${documentId}`, {
                withCredentials: true
            });
            return response.data;
        },
        onSuccess: (_, documentId) => {
            trackEvent("Delete Query", {
                "_id": documentId
            })

            queryClient.invalidateQueries(['documents']);
            queryClient.setQueryData(['documents'], (oldData) => {
                if (oldData) {
                    return oldData.filter((doc) => doc._id !== documentId);
                }
                return oldData;
            });
        },
        onError: (error) => {
            console.error("Error deleting document:", error.response.data.message);
        },
    });
};

// activity

export const useGetAllActivities = (company_id, filters = {}) => {
    return useInfiniteQuery({
        queryKey: ['activities', company_id, filters],
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            let url = `${apiUrl}/activity`;
            const params = new URLSearchParams();

            if (company_id) {
                params.append("company_id", company_id);
            }
            if (pageParam) {
                params.append("page", pageParam);
                params.append("limit", "10");
            }

            if (filters.message) {
                params.append("message", filters.message);
            }
            if (filters.query_id) {
                params.append("query_id", filters.query_id);
            }

            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });

            return {
                data: response.data.result,
                page: response.data.page,
                totalPages: Math.ceil(response.data.totalActivities / response.data.limit)
            };
        },
        getNextPageParam: (lastPage) => {
            return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
        },
        enabled: !!company_id,
    });
};

// comments

export const useGetAllCommentMails = (company_id, filters = {}) => {
    return useInfiniteQuery({
        queryKey: ['commentMails', company_id, filters],
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            let url = `${apiUrl}/comment`;
            const params = new URLSearchParams();

            if (company_id) {
                params.append("company_id", company_id);
            }
            if (pageParam) {
                params.append("page", pageParam);
                params.append("limit", "10");
            }
            params.append("type", "mail");

            if (filters.text) {
                params.append("text", filters.text);
            }
            if (filters.query_id) {
                params.append("query_id", filters.query_id);
            }

            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });

            return {
                data: response.data.result,
                page: response.data.page,
                totalPages: Math.ceil(response.data.totalComments / response.data.limit)
            };
        },
        getNextPageParam: (lastPage) => {
            return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
        },
        enabled: !!company_id,
    });
};



export const useGetAllPrimaryClientsDocs = (_id) => {
    return useQuery({
        queryKey: ['primaryClientsDocs', _id],
        queryFn: async () => {
            const response = await axios.get(`${apiUrl}/document/primary-clients?primary_client=${_id}`, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });
            return response.data;
        },
        staleTime: 1000 * 60 * 5,
    });
}

export const useGetAllCommentQueries = (company_id, filters = {}, primaryClientId) => {
    return useInfiniteQuery({
        queryKey: ['commentQueries', company_id, filters, primaryClientId],
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            let url = `${apiUrl}/comment`;
            const params = new URLSearchParams();

            if (company_id) {
                params.append("company_id", company_id);
            }
            params.append("type", "query");
            if (pageParam) {
                params.append("page", pageParam);
                params.append("limit", "10");
            }

            if (filters.text) {
                params.append("text", filters.text);
            }
            if (filters.query_id) {
                params.append("query_id", filters.query_id);
            }
            if (primaryClientId) {
                params.append("primary_client_id", primaryClientId);
            }

            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            });

            return {
                data: response.data.result,
                page: response.data.page,
                totalPages: Math.ceil(response.data.totalComments / response.data.limit)
            };
        },
        getNextPageParam: (lastPage) => {
            return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
        },
        enabled: !!company_id,
    });
};